/**
 * /Recon — public /status page (Bucket QS-1).
 *
 * Public-facing trust signal. Three regions:
 *   - Hero banner: green / amber / rose pill summarizing overall
 *     status with a one-line headline.
 *   - Components grid: per-layer status (API, Database, etc.) with
 *     a colored pill + optional detail string when degraded.
 *   - Incidents timeline: empty for now ("All clear"); will populate
 *     when an incident is posted (future bucket).
 *
 * Auto-refreshes every 30 seconds. No login required — anyone with
 * the URL can hit it.
 */
import { useQuery } from "@tanstack/react-query";
import { getPublicStatus } from "@hackereyes/api-client";
import "./status-page.css";

type ComponentStatusValue = "operational" | "degraded" | "outage";

interface Component {
  name: string;
  label: string;
  status: ComponentStatusValue;
  detail: string | null;
}

interface Incident {
  id: string;
  title: string;
  status: "investigating" | "identified" | "monitoring" | "resolved";
  started_at: string;
  resolved_at: string | null;
  body: string;
}

interface StatusPayload {
  status: ComponentStatusValue;
  checked_at: string;
  components: Component[];
  incidents: Incident[];
}

const OVERALL_COPY: Record<ComponentStatusValue, { headline: string; sub: string }> = {
  operational: {
    headline: "All systems operational",
    sub: "Every layer of /Recon is responding normally.",
  },
  degraded: {
    headline: "Degraded performance",
    sub: "One or more components are responding more slowly than usual. Scans and report renders may take longer to complete.",
  },
  outage: {
    headline: "Service outage",
    sub: "/Recon is currently unable to accept new scans or commissions. We're investigating.",
  },
};

const COMPONENT_STATUS_LABEL: Record<ComponentStatusValue, string> = {
  operational: "Operational",
  degraded: "Degraded",
  outage: "Outage",
};

function formatChecked(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function StatusPage() {
  const { data, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: ["public-status"],
    queryFn: async () => {
      const { data, error } = await getPublicStatus();
      if (error) throw new Error("status_load_failed");
      return data as unknown as StatusPayload;
    },
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  // Network-error branch — render an explicit "we can't reach the
  // status endpoint, which itself indicates a problem" message rather
  // than going silent.
  if (error && !data) {
    return (
      <main className="status-page">
        <header className="status-header">
          <h1 className="status-title">/Recon system status</h1>
        </header>
        <section className={`status-overall status-overall-outage`} role="status">
          <span className="status-pill status-pill-outage" aria-label="Outage">
            ●
          </span>
          <div>
            <div className="status-headline">Status check unreachable</div>
            <p className="status-sub">
              We couldn't reach the API to read the live status. The page
              will retry automatically. If this persists for more than a
              few minutes, the platform itself is likely down.
            </p>
          </div>
        </section>
      </main>
    );
  }

  if (isLoading || !data) {
    return (
      <main className="status-page">
        <header className="status-header">
          <h1 className="status-title">/Recon system status</h1>
        </header>
        <p className="status-loading">Checking each component…</p>
      </main>
    );
  }

  const overall = OVERALL_COPY[data.status];

  return (
    <main className="status-page">
      <header className="status-header">
        <div className="status-eyebrow">SignumCyber /Recon</div>
        <h1 className="status-title">System status</h1>
        <p className="status-sublede">
          Real-time health of the /Recon platform. Updated every 30 seconds.
        </p>
      </header>

      <section
        className={`status-overall status-overall-${data.status}`}
        role="status"
        aria-live="polite"
      >
        <span
          className={`status-pill status-pill-${data.status}`}
          aria-label={COMPONENT_STATUS_LABEL[data.status]}
        >
          ●
        </span>
        <div>
          <div className="status-headline">{overall.headline}</div>
          <p className="status-sub">{overall.sub}</p>
        </div>
      </section>

      <section className="status-components-section">
        <h2 className="status-section-title">Components</h2>
        <ul className="status-components">
          {data.components.map((c) => (
            <li key={c.name} className={`status-component status-component-${c.status}`}>
              <div className="status-component-head">
                <span className="status-component-label">{c.label}</span>
                <span
                  className={`status-component-pill status-component-pill-${c.status}`}
                  aria-label={COMPONENT_STATUS_LABEL[c.status]}
                >
                  {COMPONENT_STATUS_LABEL[c.status]}
                </span>
              </div>
              {c.detail && (
                <p className="status-component-detail">{c.detail}</p>
              )}
            </li>
          ))}
        </ul>
      </section>

      <section className="status-incidents-section">
        <h2 className="status-section-title">Recent incidents</h2>
        {data.incidents.length === 0 ? (
          <p className="status-incidents-empty">
            No incidents in the recent timeline. We post here when an
            incident is investigated, identified, or resolved.
          </p>
        ) : (
          <ol className="status-incidents">
            {data.incidents.map((inc) => (
              <li key={inc.id} className={`status-incident status-incident-${inc.status}`}>
                <div className="status-incident-head">
                  <span className="status-incident-status">{inc.status}</span>
                  <span className="status-incident-title">{inc.title}</span>
                </div>
                <p className="status-incident-body">{inc.body}</p>
                <div className="status-incident-meta">
                  Started {formatChecked(inc.started_at)}
                  {inc.resolved_at && ` · Resolved ${formatChecked(inc.resolved_at)}`}
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>

      <footer className="status-footer">
        Last checked {formatChecked(data.checked_at)}
        {dataUpdatedAt > 0 && data.checked_at && " · client refresh every 30s"}
      </footer>
    </main>
  );
}
